import type { TestRun } from "@/services/testing";

/**
 * A plan is called flaky once it has flipped between pass and fail at least this many times without
 * being edited — one flip is a fix or a regression, repeated flips are non-determinism.
 */
const FLAKY_MIN_TRANSITIONS = 2;

export interface RunHistorySummary {
	total: number;
	passed: number;
	failed: number;
	/** Flipped between pass and fail repeatedly — a timing/environment signal, not a real change. */
	flaky: boolean;
	/** Triage category of the most recent failure, when there was one. */
	lastFailureCategory: string | null;
}

/**
 * Condenses a plan's run history into the one line worth showing in chat.
 *
 * Pulled out of an inline IIFE in the chat panel so the flaky heuristic is named, testable and not
 * re-derived on every render of an unrelated part of the thread.
 *
 * Returns null when there is nothing worth saying (0 or 1 finished run).
 */
export function summarizeRunHistory(runs: TestRun[]): RunHistorySummary | null {
	const finished = runs.filter((r) => r.status === "passed" || r.status === "failed");
	if (finished.length <= 1) return null;

	// `/api/runs` returns newest-first. Sort explicitly rather than trusting the caller's ordering:
	// reading `finished[length - 1]` as "the latest run" reported the OLDEST run's triage, which
	// rendered "last failure: unknown" whenever the oldest run happened to have passed.
	const chronological = [...finished].sort(
		(a, b) => runStartedAt(a) - runStartedAt(b),
	);

	const passed = chronological.filter((r) => r.status === "passed").length;
	const failed = chronological.length - passed;
	const transitions = chronological.reduce(
		(n, run, i) => (i > 0 && run.status !== chronological[i - 1].status ? n + 1 : n),
		0,
	);

	// The most recent FAILURE — not the most recent run, which may well have passed.
	const lastFailure = [...chronological].reverse().find((r) => r.status === "failed");
	const triage = (lastFailure?.summary as Record<string, unknown> | null | undefined)?.triage as
		| { category?: string }
		| null
		| undefined;

	return {
		total: chronological.length,
		passed,
		failed,
		flaky: passed > 0 && failed > 0 && transitions >= FLAKY_MIN_TRANSITIONS,
		lastFailureCategory: lastFailure ? (triage?.category ?? "unknown") : null,
	};
}

/** Epoch ms for ordering; unparseable timestamps sort oldest so they never masquerade as latest. */
function runStartedAt(run: TestRun): number {
	const raw = (run as { started_at?: string | null }).started_at;
	const ts = raw ? Date.parse(String(raw)) : Number.NaN;
	return Number.isFinite(ts) ? ts : 0;
}
